<template>
  <div class="NameAndCategory mb-24">
    <div class="flex justify-between type-tagline">
      <div v-if="globalContent.getBrandLink(productItem.brand.code)" class="dotted-border">
        <nuxt-link
          :to="globalContent.getBrandLink(productItem.brand.code) || ''"
        >
          {{ productItem.brand.name }}
        </nuxt-link>
      </div>

      <div class="flex">
        <p v-if="showPartNo" class="type-tagline-xs text-text-subtle mb-12">
          {{ $t('productPage.partNo.short') }} {{ activeVariant.partNo }}
        </p>
        <div v-if="showPartNo && globalContent.getCountryCode === 'no'" class="mb-12 flex">
          <hr class="h-full w-[1px] mx-8 bg-bg-active">
          <p class="type-tagline-xs text-text-subtle">
            NOBB: {{ activeVariant.parametricByCode('ItemNumberNobb')?.value }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="productItem.isHiddenProduct" class="flex items-center my-16">
      <Tooltip
        v-if="showPartNo"
        :text="$t('fastorder.table.hiddenProduct')"
        :show-on-hover="true"
      >
        <img
          alt=""
          src="/icons/eye-closed.svg"
          class="w-[16px] h-[16px]"
        >
      </Tooltip>
      <img
        v-else
        alt=""
        src="/icons/eye-closed.svg"
        class="w-[16px] h-[16px]"
      >
    </div>

    <h1 class="type-headline-xl mb-12" v-text="productItem.extendedLayout ? productItem.name : activeVariant.name" />

    <div
      v-if="globalContent.getCountryCode === 'de'"
      class="mb-12 empty:hidden"
    >
      {{ productItem.parametricByCode('ProductCollectionDynamic')?.listValue?.name }}
    </div>

    <ReadMore
      v-if="showCollapsedDescription && showDescription && description"
      open-close-class="type-sm text-text-subtle underline"
    >
      <div
        class="type-sm text-text-subtle"
        v-html="description"
      />
    </ReadMore>
    <div
      v-else-if="showDescription && description"
      class="ProductDescription type-sm"
      v-html="description"
    />
    <!-- * * * * * "chips" -->
    <div v-if="chips" class="type-xs text-text-attention flex flex-wrap gap-8 mt-16">
      <div
        v-for="(chip, index) in chips"
        :key="index"
        class="inline-block bg-bg-attention px-8 py-4"
      >
        {{ chip }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductModel } from '#models/product';
import type ProductVariant from '#models/productVariant';
import { useGlobalContentStore } from '~/store/globalContent';
import Tooltip from '~/components/body/Tooltip.vue';
const globalContent = useGlobalContentStore();

interface Props {
  productItem: ProductModel,
  activeVariant: ProductVariant,
  showPartNo?: boolean;
  showDescription?: boolean;
  showCollapsedDescription?: boolean; // uses full description but with "read more"
}
const props = withDefaults(defineProps<Props>(), {
  showPartNo: true,
  showDescription: true,
});
// Makes sure brands are loaded
onMounted(()=> {
  globalContent.loadBrands();
});

const description = computed(()=> {
  if (props.showCollapsedDescription) {
    if (props.activeVariant.shortVariantDescription !== '') {
      return props.activeVariant.shortVariantDescription;
    }
    return props.productItem.shortDescription;
  }
  if (props.productItem.extendedLayout && props.productItem.description) {
    return props.productItem.description;
  }
  if (!props.productItem.extendedLayout && props.productItem.shortDescription) {
    return props.productItem.shortDescription;
  }
  return null;
});

const chips = computed<string[]>(()=> {
  // Check variant chips
  let chips = props.activeVariant.parametrics.find(f => f.code === 'Chips');
  if (!chips) {
    // fall back to product chips
    chips = props.productItem.parametricByCode('Chips');
  }

  if (chips?.value && chips.value.trim() !== '') {
    return chips.value.split(',');
  }
  return [];
});

</script>
